import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { contentFieldTypes, contentTypesGet } from '@/shared/api/generated/content/content';
import type { FieldBody, TypeBody } from '@/shared/api/generated/model';

import { FieldFormDialog } from './FieldFormDialog';
import { FieldTree } from './FieldTree';
import { FieldTypeDialog } from './FieldTypeDialog';

/**
 * Поля модели или компонента — вкладка «Поля» деталки. Любая правка, которая
 * может задеть данные, сначала говорит, что именно она заденет.
 */
export function TypeFields({ type, readOnly = false }: { type: TypeBody; readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const [creatingIn, setCreatingIn] = useState<{ parent: FieldBody | undefined }>();
  const [editingField, setEditingField] = useState<FieldBody>();
  const [retypingField, setRetypingField] = useState<FieldBody>();

  const details = useQuery({
    queryKey: ['content-type', type.id],
    queryFn: async () => {
      const response = await contentTypesGet(type.id);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос типа');
      }

      return response.data;
    },
  });

  // Виды полей приходят с сервера: набор закрыт кодом бэкенда.
  const fieldTypes = useQuery({
    queryKey: ['field-types'],
    staleTime: Infinity,
    queryFn: async () => {
      const response = await contentFieldTypes();

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос видов полей');
      }

      return response.data.items ?? [];
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['content-types', type.site_id] });
    void queryClient.invalidateQueries({ queryKey: ['content-type'] });
    void queryClient.invalidateQueries({ queryKey: ['block-types', type.site_id] });
  };

  const fields = details.data?.fields ?? [];
  const tabs = [...new Set(fields.map((item) => item.tab))];
  // Версия для перестановки полей — из ответа с полями.
  const typeForOrder = details.data?.type ?? type;

  if (details.isPending) {
    return <p>Загружаем поля…</p>;
  }

  return (
    <>
      <FieldTree
        type={typeForOrder}
        fields={fields}
        onAdd={(parent) => {
          setCreatingIn({ parent });
        }}
        onEdit={setEditingField}
        onRetype={setRetypingField}
        onChanged={refresh}
        readOnly={readOnly}
      />

      {creatingIn !== undefined && (
        <FieldFormDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setCreatingIn(undefined);
            }
          }}
          typeID={type.id}
          typeKind={type.kind}
          parent={creatingIn.parent}
          siblings={fields.filter((item) => item.parent_id === creatingIn.parent?.id)}
          tabs={tabs}
          fieldTypes={fieldTypes.data ?? []}
          onDone={refresh}
        />
      )}

      {editingField !== undefined && (
        <FieldFormDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setEditingField(undefined);
            }
          }}
          typeID={type.id}
          typeKind={type.kind}
          field={editingField}
          parent={fields.find((item) => item.id === editingField.parent_id)}
          siblings={fields.filter((item) => item.parent_id === editingField.parent_id)}
          tabs={tabs}
          fieldTypes={fieldTypes.data ?? []}
          onDone={refresh}
        />
      )}

      {retypingField !== undefined && (
        <FieldTypeDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setRetypingField(undefined);
            }
          }}
          field={retypingField}
          fieldTypes={fieldTypes.data ?? []}
          onDone={refresh}
        />
      )}
    </>
  );
}
